// The showcase rooms the pre-upsell page draws — and the one seam you edit
// when you add or replace one.
//
// To swap a room in:
//   1. Design it in the editor, panels included, and save it.
//   2. Import the seed + recompute both variants (docs/templates.md):
//        docker compose exec backend python precompute_templates.py \
//          --import-scene TOKEN --id music-studio --write-snapshots
//   3. node scripts/extract-upsell-curves.mjs
//
// Nothing else changes. The 3D comes from the seed doc directly (UpsellRoom
// renders it with the editor's own viewport, so there are no images to
// produce or keep in step); curves, headline figures and the graphs all come
// out of step 3. The page only ever reads this module.

import type { EditorDoc } from '@roomtreatment/core';
import type { OnboardingGoalId } from '@roomtreatment/room-flow';
import { ONBOARDING_EXAMPLE_ROOMS } from '@/components/room/guided/onboarding/exampleRooms';
import curves from './curves.generated.json';

export interface RoomCurves {
  /** True while the room's `treated` snapshot still has zero panels — the
   *  after-curve is then a drawing of what absorption does, not a solve.
   *  Design pages badge it; it clears itself once the room is designed. */
  provisional: boolean;
  panels: number;
  freq: { lo: number; hi: number; before: number[]; after: number[] };
  rt60: { bands: number[]; before: number[]; after: number[] };
  metrics: {
    bassSwingDb: { before: number; after: number };
    windowDb: { before: number; after: number };
    decayS: { before: number; after: number };
    symmetryPct: { before: number; after: number };
  };
}

export interface ShowcaseRoom {
  /** Matches contracts/templates/example-rooms.json — keep them in step. */
  id: string;
  name: string;
  blurb: string;
  curves: RoomCurves;
  /** The room itself, rendered live by UpsellRoom. Read from the SAME seed
   *  file precompute_templates.py reads, so the 3D on this screen and the
   *  curves under it are the same room by construction — not two artefacts
   *  someone has to remember to regenerate together. */
  doc: EditorDoc;
}

const CURVES = curves.rooms as Record<string, RoomCurves>;

/** Demo dressing for a seed room that has not been designed yet.
 *
 *  The four seed rooms ship with `panels: []`, so Before and After render the
 *  same picture and the wipe has nothing to reveal. This puts a plausible
 *  treatment and a piece of furniture in the room so the comparison can be
 *  looked at — and it is a no-op the moment the room is designed for real
 *  (`--import-scene`, see the header), because a doc that already has panels
 *  is returned untouched. Same self-erasing arrangement as the provisional
 *  curves, and the same `provisional` flag covers both.
 *
 *  Positions and rotations follow the solver's own surface convention, the
 *  one `scene/panelSurface.check.ts` pins: a panel sits half its depth off
 *  its wall and its +Z faces into the room. */
const PANEL = { w: 0.6, h: 1.2, d: 0.1 };
const CLOUD = { w: 1.2, h: 0.6, d: 0.1 };

function demoPanels({ Lx, Ly, H }: EditorDoc['dims']): EditorDoc['panels'] {
  const base = { sku: null, label: null, subkind: null, mounting: 'wall' as const };
  const wall = (id: string, position: [number, number, number], yaw: number) => ({
    ...base, id, size: PANEL, position, rotation: [0, yaw, 0] as [number, number, number],
  });
  return [
    // First reflections, both side walls, mirrored.
    wall('sc-l1', [PANEL.d / 2, 1.2, Ly * 0.34], Math.PI / 2),
    wall('sc-l2', [PANEL.d / 2, 1.2, Ly * 0.58], Math.PI / 2),
    wall('sc-r1', [Lx - PANEL.d / 2, 1.2, Ly * 0.34], -Math.PI / 2),
    wall('sc-r2', [Lx - PANEL.d / 2, 1.2, Ly * 0.58], -Math.PI / 2),
    // Front wall, flanking the speakers.
    wall('sc-f1', [Lx * 0.26, 1.3, PANEL.d / 2], 0),
    wall('sc-f2', [Lx * 0.74, 1.3, PANEL.d / 2], 0),
    // Rear wall.
    wall('sc-b1', [Lx * 0.3, 1.4, Ly - PANEL.d / 2], Math.PI),
    wall('sc-b2', [Lx * 0.7, 1.4, Ly - PANEL.d / 2], Math.PI),
    // Ceiling cloud over the seat.
    {
      ...base, id: 'sc-c1', mounting: 'ceiling' as const, size: CLOUD,
      position: [Lx / 2, H - CLOUD.d / 2, Ly * 0.44] as [number, number, number],
      rotation: [Math.PI / 2, 0, 0] as [number, number, number],
    },
  ];
}

/** Kenney sofa, rear of the room. Present in BOTH states — furniture is not
 *  treatment, and a room with something in it reads as a room. */
function demoFurniture({ Lx, Ly }: EditorDoc['dims']): EditorDoc['furniture'] {
  const size = { w: 1.76, h: 0.83, d: 0.74 };
  return [{
    id: 'sc-sofa',
    presetId: 'loungeSofa',
    size,
    position: [Lx / 2, size.h / 2, Ly * 0.82],
    rotation: [0, Math.PI, 0],
  }];
}

function showcaseDoc(doc: EditorDoc): EditorDoc {
  if (doc.panels.length > 0) return doc;
  return {
    ...doc,
    panels: demoPanels(doc.dims),
    furniture: doc.furniture.length > 0 ? doc.furniture : demoFurniture(doc.dims),
  };
}

const NAMES: Record<string, string> = {
  'home-theater': 'Home theater',
  'music-studio': 'Music studio',
  'l-shape-living': 'Listening room',
  'vaulted-theater': 'Vaulted theater',
};

const ROOMS: Record<string, ShowcaseRoom> = Object.fromEntries(
  ONBOARDING_EXAMPLE_ROOMS
    .filter((r) => CURVES[r.id])
    .map((r) => [r.id, {
      id: r.id,
      name: NAMES[r.id] ?? r.name,
      blurb: r.blurb,
      curves: CURVES[r.id],
      doc: showcaseDoc(r.doc),
    }]),
);

/** Which room the user is shown, from the goal they picked in onboarding. */
const GOAL_ROOM: Record<OnboardingGoalId, string> = {
  'theater': 'home-theater',
  'music-studio': 'music-studio',
  'recording-studio': 'music-studio',
  'listening-space': 'l-shape-living',
  'other': 'l-shape-living',
};

export function roomForGoal(goal: OnboardingGoalId | null): ShowcaseRoom {
  return ROOMS[GOAL_ROOM[goal ?? 'other']] ?? ROOMS['l-shape-living'];
}

export const ALL_ROOMS = Object.values(ROOMS);
